"use client";

import React, { useState } from "react";
import { Mail, MapPin, Phone, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export default function ContactUsContent() {
  const [loading, setLoading] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Simulate API call
    setTimeout(() => {
      setLoading(false);
      toast.success("Message sent successfully!", {
        description: "We'll get back to you within 24 hours.",
      });
      // Reset form logic here if needed
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      
      {/* Page Header */}
      <div className="text-center max-w-2xl mx-auto mb-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Get in touch</h1>
        <p className="text-gray-600">
          We&apos;d love to hear from you. Please fill out this form or shoot us an email.
        </p>
      </div>

      <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8">
        
        {/* Left: Contact Info */}
        <div className="bg-primary text-white rounded-2xl p-8 md:p-12 shadow-lg flex flex-col justify-between overflow-hidden relative">
          {/* Decorative Circle */}
          <div className="absolute top-0 right-0 -mt-10 -mr-10 w-40 h-40 bg-white/10 rounded-full blur-2xl"></div>
          <div className="absolute bottom-0 left-0 -mb-10 -ml-10 w-40 h-40 bg-white/10 rounded-full blur-2xl"></div>

          <div className="relative z-10">
            <h2 className="text-2xl font-bold mb-6">Contact Information</h2>
            <p className="text-blue-100 mb-10 leading-relaxed">
              Have a query about our services or need help with your account? 
              Our team is ready to assist you.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-white/10 rounded-lg">
                  <Mail className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Email</h3>
                  <p className="text-blue-100">hello@cardbox.in</p>
                  <p className="text-blue-100">support@cardbox.in</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="p-2 bg-white/10 rounded-lg">
                  <MapPin className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Office</h3>
                  <p className="text-blue-100">
                    CardBox HQ, Tech Park<br />
                    Patna, Bihar 800001<br />
                    India
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="p-2 bg-white/10 rounded-lg">
                  <Phone className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Phone</h3>
                  <p className="text-blue-100">01169269691</p>
                  <p className="text-xs text-blue-200 mt-1">Mon-Fri from 8am to 5pm</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="relative z-10 mt-12">
             <p className="text-sm text-blue-200">Connect with us on social media</p>
             <div className="flex gap-4 mt-4">
                {/* Social Placeholders */}
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 cursor-pointer transition">IG</div>
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 cursor-pointer transition">TW</div>
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 cursor-pointer transition">LN</div>
             </div>
          </div>
        </div>

        {/* Right: Contact Form */}
        <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg border border-gray-100">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">First Name</label>
                <Input required placeholder="John" className="bg-gray-50" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Last Name</label>
                <Input required placeholder="Doe" className="bg-gray-50" />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Email Address</label>
              <Input required type="email" placeholder="john@example.com" className="bg-gray-50" />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Subject</label>
              <Input required placeholder="How can we help?" className="bg-gray-50" />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Message</label>
              <Textarea 
                required 
                placeholder="Tell us more about your inquiry..." 
                className="min-h-[150px] bg-gray-50" 
              />
            </div>

            <Button type="submit" disabled={loading} className="w-full h-12 text-base">
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending...
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" /> Send Message
                </>
              )}
            </Button>
          </form>
        </div>

      </div>
    </div>
  );
}
